import Button from '@codegouvfr/react-dsfr/Button';
import Card from '@codegouvfr/react-dsfr/Card';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import clsx from 'clsx';
import { DepartmentLabels } from 'maestro-shared/referential/Department';
import { matrixKindLabel as getMatrixKindLabel } from 'maestro-shared/schema/Matrix/MatrixReferential';
import { ContextLabels } from 'maestro-shared/schema/ProgrammingPlan/Context';
import {
  isCreatedPartialSample,
  isDeletableSample,
  type PartialSample,
  type PartialSampleToCreate
} from 'maestro-shared/schema/Sample/Sample';
import { getSampleMatrixLabel } from 'maestro-shared/schema/Sample/SampleMatrix';
import { useState } from 'react';
import { SampleStatusBadge } from 'src/components/SampleStatusBadge/SampleStatusBadge';
import RemoveSample from 'src/components/SampleTable/RemoveSample';
import { useAuthentication } from 'src/hooks/useAuthentication';
import { useMatrixReferential } from 'src/hooks/useMatrixReferential';
import { useSamplesLink } from 'src/hooks/useSamplesLink';
import useWindowSize from 'src/hooks/useWindowSize';
import './SampleCard.scss';
import { formatMaestroDate } from 'maestro-shared/utils/date';

type Props = {
  sample: PartialSample | PartialSampleToCreate;
  horizontal?: boolean;
};

const SampleCard = ({ sample, horizontal }: Props) => {
  const { sampleLink } = useSamplesLink();
  const { user, hasUserPermission } = useAuthentication();
  const { isMobile } = useWindowSize();

  const [isExpanded, setIsExpanded] = useState(!isMobile);

  return (
    <Card
      {...(horizontal ? { enlargeLink: true } : { enlargeLink: false })}
      className="sample-card"
      start={
        !horizontal && (
          <div className={clsx('d-flex-align-start')}>
            <div className="flex-grow-1">
              <SampleStatusBadge sample={sample} />
            </div>
            {isMobile &&
              (isExpanded ? (
                <Button
                  title="Réduire"
                  iconId="fr-icon-subtract-line"
                  size="small"
                  priority="tertiary"
                  onClick={() => setIsExpanded(false)}
                />
              ) : (
                <Button
                  title="Voir plus"
                  iconId="fr-icon-add-line"
                  size="small"
                  priority="tertiary"
                  onClick={() => setIsExpanded(true)}
                />
              ))}
          </div>
        )
      }
      title={
        isCreatedPartialSample(sample) ? (
          <div className={clsx('d-flex-align-center')}>
            <div className="flex-grow-1">{sample.reference}</div>
            {horizontal && <SampleStatusBadge sample={sample} />}{' '}
          </div>
        ) : (
          'Hors ligne'
        )
      }
      classes={
        horizontal
          ? {
              start: cx('fr-hidden'),
              link: 'flex-grow-1',
              content: cx('fr-p-2w', 'fr-pb-5v'),
              desc: cx('fr-mt-0')
            }
          : {}
      }
      border
      linkProps={{
        to: sampleLink(sample.id)
      }}
      desc={
        <span className={cx('fr-text--xs', 'fr-mb-0')}>
          {horizontal ? (
            <>
              <span className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
                <span className={cx('fr-col-6')}>
                  <CompanyBlock sample={sample} />
                </span>
                <span className={cx('fr-col-6')}>
                  <ContextBlock sample={sample} />
                </span>
              </span>
              <span className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
                <span className={cx('fr-col-6')}>
                  <SamplerBlock sample={sample} userName={user?.name} />
                </span>
                <span className={cx('fr-col-6')}>
                  <MatrixBlock sample={sample} />
                </span>
              </span>
            </>
          ) : (
            <>
              <CompanyBlock sample={sample} />
              {isExpanded && (
                <>
                  <DateBlock sample={sample} />
                  <DepartmentBlock sample={sample} />
                  <ContextBlock sample={sample} />
                  <MatrixBlock sample={sample} />
                  <SamplerBlock sample={sample} userName={user?.name} />
                </>
              )}
            </>
          )}
        </span>
      }
      size="small"
      titleAs="h6"
      end={
        isExpanded &&
        !horizontal && (
          <div className={clsx('d-flex-align-center')}>
            <Button
              size="small"
              className={clsx('fr-mr-2w')}
              linkProps={{
                to: sampleLink(sample.id)
              }}
              priority={sample.step !== 'Sent' ? 'primary' : 'secondary'}
            >
              {sample.step !== 'Sent' ? 'A compléter' : 'Consulter'}
            </Button>
            {hasUserPermission('deleteSample') && isDeletableSample(sample) && (
              <RemoveSample sample={sample} />
            )}
          </div>
        )
      }
    />
  );
};

const CompanyBlock = ({
  sample
}: {
  sample: PartialSample | PartialSampleToCreate;
}) => {
  return (
    <span className="icon-text">
      <span className={cx('fr-icon-map-pin-2-line', 'fr-icon--sm')}></span>
      <span>
        {sample.company ? (
          sample.company.name
        ) : (
          <>
            {sample.companyOffline}
            <span className="missing-data">Information à compléter</span>
          </>
        )}
      </span>
    </span>
  );
};

const DateBlock = ({
  sample
}: {
  sample: PartialSample | PartialSampleToCreate;
}) => {
  return (
    <span className="icon-text">
      <span className={cx('fr-icon-calendar-event-line', 'fr-icon--sm')}></span>
      {sample.sampledDate ? (
        formatMaestroDate(sample.sampledDate)
      ) : (
        <span className="missing-data">Information à compléter</span>
      )}
    </span>
  );
};

const DepartmentBlock = ({
  sample
}: {
  sample: PartialSample | PartialSampleToCreate;
}) => {
  return (
    <span className="icon-text">
      <span className={cx('fr-icon-road-map-line', 'fr-icon--sm')}></span>
      {sample.department ? (
        <>
          {DepartmentLabels[sample.department]} ({sample.department})
        </>
      ) : (
        <span className="missing-data">Information à compléter</span>
      )}
    </span>
  );
};

const ContextBlock = ({
  sample
}: {
  sample: PartialSample | PartialSampleToCreate;
}) => {
  return (
    <span className="icon-text">
      <span className={cx('fr-icon-microscope-line', 'fr-icon--sm')}></span>
      {sample.context ? (
        ContextLabels[sample.context]
      ) : (
        <span className="missing-data">Information à compléter</span>
      )}
    </span>
  );
};

const MatrixBlock = ({
  sample
}: {
  sample: PartialSample | PartialSampleToCreate;
}) => {
  const matrixReferential = useMatrixReferential();
  const matrixFullLabel = (sample.matrices ?? [])
    .flatMap((sampleMatrix) => {
      if (!sampleMatrix.matrixKind) {
        return [];
      }
      const matrixKindLabel = getMatrixKindLabel(
        matrixReferential,
        sampleMatrix.matrixKind
      );
      const matrixLabel = getSampleMatrixLabel(matrixReferential, sampleMatrix);
      return [
        matrixLabel !== '' && matrixLabel !== matrixKindLabel
          ? `${matrixKindLabel} - ${matrixLabel}`
          : matrixKindLabel
      ];
    })
    .join(', ');

  return matrixFullLabel ? (
    <span className="icon-text">
      <span className={cx('fr-icon-restaurant-line', 'fr-icon--sm')}></span>
      {matrixFullLabel}
    </span>
  ) : null;
};

const SamplerBlock = ({
  sample,
  userName
}: {
  sample: PartialSample | PartialSampleToCreate;
  userName?: string | null;
}) => {
  return (
    <span className="icon-text">
      <span className={cx('fr-icon-user-line', 'fr-icon--sm')}></span>
      {isCreatedPartialSample(sample)
        ? `${sample.sampler.name}`
        : `${userName}`}
    </span>
  );
};

export default SampleCard;
